/**
 * ShiftGateway の Supabase 実装。
 *
 * - Edge Function: supabase.functions.invoke。
 * - 目的特化の結合クエリ: 汎用 repository(table) の select 埋め込み（!inner）で実装
 *   （方針B改訂・2026-06-01）。
 */
import { supabase } from "@/integrations/supabase/client";
import { repository } from "@/infrastructure/repositories";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import type {
  ShiftGateway,
  StaffHolidayRow,
  StaffWorkdayPatternRow,
} from "@/domain/repositoryInterface/ShiftGateway";

export class SupabaseShiftGateway implements ShiftGateway {
  async bulkGenerateShifts(params: { yearMonth: string; staffIds: string[] }): Promise<{ generated: number }> {
    const { data, error } = await supabase.functions.invoke("bulk-generate-shifts", {
      body: { yearMonth: params.yearMonth, staffIds: params.staffIds },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return { generated: data?.generated ?? 0 };
  }

  async staffWorkdayPatterns(shopId: string | null): Promise<StaffWorkdayPatternRow[]> {
    // 自店舗（非削除）スタッフのパターンだけに DB 側で絞る（staff!inner 結合）。
    const where: Condition[] = [{ column: "staff.deleted_at", op: "isNull" }];
    if (shopId) where.push({ column: "staff.shop_id", op: "eq", value: shopId });

    const { data } = await repository("staff_workday_patterns").findMany<StaffWorkdayPatternRow>({
      select: "*, staff!inner(shop_id)",
      where,
    });
    return data;
  }

  async staffHolidays(
    shopId: string,
    startDate: string,
    endDate: string,
  ): Promise<StaffHolidayRow[]> {
    const { data } = await repository("staff_holidays").findMany<StaffHolidayRow>({
      select: "*, staff!inner(shop_id)",
      where: [
        { column: "staff.deleted_at", op: "isNull" },
        { column: "staff.shop_id", op: "eq", value: shopId },
        { column: "date", op: "gte", value: startDate },
        { column: "date", op: "lte", value: endDate },
      ],
    });
    return data;
  }
}
